import classNames from 'clsx';
import type { FormApi, SubmissionErrors } from 'final-form';
import { isNumber } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import createForm from '@swf/components/Forms/Form';
import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import StarsInfo from '@swf/components/StarsInfo/StarsInfo';
import Translation from '@swf/components/Translation/Translation';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';
import { License } from '@swf/core/types/Account';

import useStateReducer, { DispatchActionType } from '../../../hooks/useStateReducer';

import {
  TXTCLI_1_hour,
  TXTCLI_Apply__forChanges,
  TXTCLI_N0_hours,
  TXTCLI_N0_minutes,
  TXTWCL_An_unexpected_error_occurred_w_ELL_1,
  TXTWCL_If_you_likeX_you_can_set_up_a__ELL,
  TXTWCL_No_reminder,
  TXTWCL_This_means_weXll_check_in_with_ELL,
  TXTWCL_You_just_have_to_choose_how_of_ELL,
  TXTWCL_Your_changes_have_been_saved_s_ELL
} from './CasinoSessionReminderForm.messages.json';

const dropdownValues = {
  [License.UK]: [
    { text: <Translation {...TXTWCL_No_reminder} />, value: 0 },
    { text: <Translation values={{ 0: '15' }} {...TXTCLI_N0_minutes} />, value: 15 },
    { text: <Translation values={{ 0: '30' }} {...TXTCLI_N0_minutes} />, value: 30 },
    { text: <Translation values={{ 0: '1' }} {...TXTCLI_1_hour} />, value: 60 },
    { text: <Translation values={{ 0: '2' }} {...TXTCLI_N0_hours} />, value: 120 }
  ],
  default: [
    { text: <Translation {...TXTWCL_No_reminder} />, value: 0 },
    { text: <Translation values={{ 0: '1' }} {...TXTCLI_1_hour} />, value: 60 }
  ]
};

interface FormValues {
  minutes: number;
}

const Form = createForm<FormValues>();

const CasinoSessionReminderForm: React.FC<{ notification?: boolean }> = ({ notification }) => {
  const request = useRequest();
  const [minutes, setMinutes] = useState(0);
  const license = useSelector(select.account.getLicense);
  const [{ response, error }, dispatchAction] = useStateReducer<FormValues>();

  const fetchCasinoLimits = useCallback(
    (successCallback?: () => void) =>
      request({ service: 'responsiblegaming.casinosessionreminder' }).subscribe(({ body }) => {
        setMinutes(body);
        successCallback?.();
      }),
    [request]
  );

  useEffect(() => {
    fetchCasinoLimits();
  }, [fetchCasinoLimits]);

  const handleSubmit = (
    { minutes }: FormValues,
    { initialize }: FormApi<FormValues>,
    callback?: (errors?: SubmissionErrors) => void
  ) => {
    request({
      service: 'responsiblegaming.updatecasinosessionreminder',
      body: { minutes }
    }).subscribe(
      ({ body }) => {
        callback?.();
        fetchCasinoLimits(() => initialize({ minutes }));
        dispatchAction({ type: DispatchActionType.Response, payload: body });
      },
      () => {
        callback?.();
        dispatchAction({ type: DispatchActionType.Error });
      }
    );
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{ minutes }}
      render={({ pristine, submitting }) => (
        <div
          className={classNames(
            { [sharedStyles.restrictedWidth]: !notification },
            sharedStyles.formWithDropdown
          )}
        >
          {isNumber(response) && (
            <StarsInfo type="confirmation" className={sharedStyles.message}>
              <Translation {...TXTWCL_Your_changes_have_been_saved_s_ELL} />
            </StarsInfo>
          )}
          {error && (
            <StarsInfo type="error" className={sharedStyles.message}>
              <Translation {...TXTWCL_An_unexpected_error_occurred_w_ELL_1} />
            </StarsInfo>
          )}
          <Translation tagName="p" {...TXTWCL_If_you_likeX_you_can_set_up_a__ELL} />
          <Translation tagName="p" {...TXTWCL_This_means_weXll_check_in_with_ELL} />
          <Translation tagName="p" {...TXTWCL_You_just_have_to_choose_how_of_ELL} />
          <StarsDropDown
            hideLabel
            id="minutes"
            label="Session Reminder"
            options={dropdownValues[license] || dropdownValues.default}
          />
          <StarsButton
            block
            type="submit"
            disabled={pristine}
            loading={submitting}
            className={sharedStyles.submit}
            translation={TXTCLI_Apply__forChanges}
          />
        </div>
      )}
    />
  );
};

export default CasinoSessionReminderForm;
